import {useCallback, useEffect, useMemo, useRef} from "react";
import {
  GetBatchOptimizationResultQuery,
  RouteOptimizationResultItem,
  useGetBatchOptimizationResultLazyQuery
} from "../generated/graphql";
import {Constants} from "../constants/constants";

/**
 * Configuration options for the batch optimization hook
 */
export interface UseBatchOptimizationConfig {
  /**
   * Threshold for enabling batch optimization mode.
   * When stopCount >= this value, batch mode is enabled.
   * Set to 0 to disable batch optimization.
   */
  routeOptimizationBatchModeStops: number;

  /**
   * Current stop count from the selected manifest
   */
  stopCount: number;
}

/**
 * Callbacks triggered by batch optimization events
 */
export interface UseBatchOptimizationCallbacks {
  /**
   * Called when batch optimization result is successfully received
   */
  onSuccess: (result: RouteOptimizationResultItem) => void;

  /**
   * Called when batch optimization encounters an error
   */
  onError: (error: unknown) => void;
}

/**
 * Return type for the useBatchOptimization hook
 */
export interface UseBatchOptimizationResult {
  /**
   * Whether batch optimization mode is enabled based on stop count
   */
  isBatchOptimizationEnabled: boolean;

  /**
   * Whether batch optimization polling is currently active
   */
  isPolling: boolean;

  /**
   * Current job ID being polled (if any)
   */
  currentJobId: string | undefined;

  /**
   * Raw query data from the batch optimization result
   */
  data: GetBatchOptimizationResultQuery | undefined;

  /**
   * Start polling for batch optimization results
   * @param jobId - The GraphHopper job ID to poll for
   */
  startBatchPolling: (jobId: string) => void;

  /**
   * Stop polling and cleanup
   */
  stopBatchPolling: () => void;
}

/**
 * Custom hook to handle batch optimization logic for route optimization.
 *
 * This hook manages the polling mechanism for batch route optimization results
 * when the number of stops exceeds a configurable threshold. In batch mode,
 * route optimization is processed asynchronously and results are fetched
 * via polling until the optimization is complete.
 *
 * @param config - Configuration for batch optimization behavior
 * @param callbacks - Callbacks for handling success and error states
 * @returns Batch optimization state and control functions
 *
 * @example
 * ```tsx
 * const {
 *   isBatchOptimizationEnabled,
 *   startBatchPolling,
 *   stopBatchPolling,
 *   isPolling
 * } = useBatchOptimization(
 *   {
 *     routeOptimizationBatchModeStops: 50,
 *     stopCount: manifest?.stopCount ?? 0
 *   },
 *   {
 *     onSuccess: (result) => handleOptimizeRouteSuccessResponse(result),
 *     onError: (error) => handleOptimizeRouteErrorResponse(error)
 *   }
 * );
 * ```
 */
export const useBatchOptimization = (
  config: UseBatchOptimizationConfig,
  callbacks: UseBatchOptimizationCallbacks
): UseBatchOptimizationResult => {
  const {routeOptimizationBatchModeStops, stopCount} = config;
  const {onSuccess, onError} = callbacks;

  // Refs to track polling state without causing re-renders
  const isPollingBatchOptimizationResultRef = useRef<boolean>(false);
  const batchOptimizationJobIdRef = useRef<string>();

  // Apollo lazy query for fetching batch optimization results
  const [
    getBatchOptimizationResult,
    {data: batchOptimizationData, error: batchOptimizationError, startPolling, stopPolling}
  ] = useGetBatchOptimizationResultLazyQuery();

  /**
   * Determines if batch optimization mode should be enabled
   * based on the configured threshold and current stop count
   */
  const isBatchOptimizationEnabled = useMemo(() => {
    return routeOptimizationBatchModeStops > 0 && stopCount >= routeOptimizationBatchModeStops;
  }, [routeOptimizationBatchModeStops, stopCount]);

  /**
   * Start polling for batch optimization results.
   * This initiates the first query and then starts regular polling.
   */
  const startBatchPolling = useCallback(
    (jobId: string) => {
      isPollingBatchOptimizationResultRef.current = true;
      batchOptimizationJobIdRef.current = jobId;
      getBatchOptimizationResult({variables: {jobId}}).then(() => {
        if (isPollingBatchOptimizationResultRef.current) {
          startPolling(Constants.BATCH_OPTIMIZATION_POLLING_INTERVAL);
        }
      });
    },
    [getBatchOptimizationResult, startPolling]
  );

  /**
   * Stop batch optimization polling and cleanup refs.
   * This should be called when optimization completes, errors, or is cancelled.
   */
  const stopBatchPolling = useCallback(() => {
    isPollingBatchOptimizationResultRef.current = false;
    batchOptimizationJobIdRef.current = undefined;
    stopPolling();
  }, [stopPolling]);

  /**
   * Effect to handle batch optimization results.
   * Monitors polling data and triggers appropriate callbacks
   * when optimization completes or errors.
   */
  useEffect(() => {
    // Only process if we're actively polling
    if (!isPollingBatchOptimizationResultRef.current) return;

    // Handle errors
    if (batchOptimizationError) {
      stopBatchPolling();
      onError(batchOptimizationError);
      return;
    }

    // Check if optimization is finished
    const resultStatus = batchOptimizationData?.getBatchOptimizationResult?.items.at(0)?.solution?.status;

    if (resultStatus === "finished") {
      stopBatchPolling();
      const batchResultItem = batchOptimizationData?.getBatchOptimizationResult?.items.at(
        0
      ) as RouteOptimizationResultItem;
      if (batchResultItem) {
        onSuccess(batchResultItem);
      }
    }
    // When resultStatus exists but isn't "finished", optimization is still in progress
  }, [batchOptimizationData, batchOptimizationError, onError, onSuccess, stopBatchPolling]);

  /**
   * Cleanup effect - ensure polling stops when component unmounts
   */
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isBatchOptimizationEnabled,
    isPolling: isPollingBatchOptimizationResultRef.current,
    currentJobId: batchOptimizationJobIdRef.current,
    data: batchOptimizationData,
    startBatchPolling,
    stopBatchPolling
  };
};
